import { create } from 'zustand';

/**
 * Voice Filters Store - UI State Only
 * 
 * NOTE: isPitchDisabled, isSpeedDisabled, etc. are NOT stored here!
 * They are computed properties derived from voice/audioProfile.
 * Use the helper functions exported below to calculate them.
 */

interface VoiceFiltersStore {
  // UI Filter state (for voice picker filtering)
  language: string | null;
  gender: string | null;
  quality: string | null;
  style: string | null;
  
  // Modal visibility states
  showDeleteConfirmation: boolean;
  showUpdateConfirmation: boolean;
  showEditorVoiceProfiles: boolean;
  showSaveAudioProfileModal: boolean;
  
  // Editor voice picker tab state
  editorVoiceTab: 'voices' | 'profiles' | 'cloned';
  
  // Actions
  setLanguage: (language: string | null) => void;
  setGender: (gender: string | null) => void;
  setQuality: (quality: string | null) => void;
  setStyle: (style: string | null) => void;
  resetFilters: () => void;
  
  // Modal actions
  setShowDeleteConfirmation: (show: boolean) => void;
  setShowUpdateConfirmation: (show: boolean) => void;
  setShowEditorVoiceProfiles: (show: boolean) => void;
  setShowSaveAudioProfileModal: (show: boolean) => void;
  
  // Tab actions
  setEditorVoiceTab: (tab: 'voices' | 'profiles' | 'cloned') => void;
}

export const useVoiceFiltersStore = create<VoiceFiltersStore>()((set) => ({
  // Initial state - UI filters only
  language: null,
  gender: null,
  quality: null,
  style: null,
  
  // Modal states
  showDeleteConfirmation: false,
  showUpdateConfirmation: false,
  showEditorVoiceProfiles: false,
  showSaveAudioProfileModal: false,
  
  // Editor voice tab state
  editorVoiceTab: 'voices',
  
  // Filter actions
  setLanguage: (language) => set({ language }),
  setGender: (gender) => set({ gender }),
  setQuality: (quality) => set({ quality }),
  setStyle: (style) => set({ style }),
  
  resetFilters: () =>
    set({
      language: null,
      gender: null,
      quality: null,
      style: null,
    }),
  
  // Modal actions
  setShowDeleteConfirmation: (show) => set({ showDeleteConfirmation: show }),
  setShowUpdateConfirmation: (show) => set({ showUpdateConfirmation: show }),
  setShowEditorVoiceProfiles: (show) => set({ showEditorVoiceProfiles: show }),
  setShowSaveAudioProfileModal: (show) => set({ showSaveAudioProfileModal: show }),
  
  // Tab actions
  setEditorVoiceTab: (tab) => set({ editorVoiceTab: tab }),
}));

// ============================================================================
// COMPUTED PROPERTIES - Helper Functions
// ============================================================================

/**
 * Voice capabilities based on platform/engine
 * These are DERIVED values, not state!
 */

interface AudioProfile {
  platform?: string;
  engine?: string;
  [key: string]: any;
}

interface Voice {
  platform: string;
  engine: string;
  styles: string[];
  [key: string]: any;
}

/**
 * Platform/Engine Support Matrix:
 * - Polly Standard: pitch ✓, speed ✓, style ✗, breathing ✓, pause ✓
 * - Polly Neural: pitch ✗, speed ✓, style ✓, breathing ✗, pause ✓
 * - Azure: pitch ✓, speed ✓, style ✓, breathing ✗, pause ✓
 * - Google: pitch ✓, speed ✓, style ✗, breathing ✗, pause ✓
 * - Emotional (Qwen): pitch ✗, speed ✗, style ✗, breathing ✗, pause ✓ (via chunked synthesis + silence-splice, not SSML — supported for every engine, no disabled-check needed)
 */

export const isPitchDisabled = (audioProfile?: AudioProfile, voice?: Voice | null): boolean => {
  if (!audioProfile?.engine) return false;

  const { platform, engine } = audioProfile;

  if (engine === 'emotional') return true;

  return platform === 'polly' && engine === 'neural';
};

export const isSpeedDisabled = (audioProfile?: AudioProfile): boolean => {
  if (!audioProfile?.engine) return false;
  
  return audioProfile.engine === 'emotional';
};

export const isStyleDisabled = (audioProfile?: AudioProfile, voice?: Voice | null): boolean => {
  if (!audioProfile?.engine) return false;
  
  // No styles available OR emotional voices
  return (voice?.styles?.length === 0) || audioProfile.engine === 'emotional';
};

export const isBreathingDisabled = (audioProfile?: AudioProfile): boolean => {
  if (!audioProfile?.platform || !audioProfile?.engine) return false;
  
  const { platform, engine } = audioProfile;
  // Only Polly Standard supports breathing
  return !(platform === 'polly' && engine === 'standard');
};

/**
 * Get all disabled flags at once (convenience function)
 */
export const getDisabledFlags = (audioProfile?: AudioProfile, voice?: Voice | null) => {
  return {
    isPitchDisabled: isPitchDisabled(audioProfile, voice),
    isSpeedDisabled: isSpeedDisabled(audioProfile),
    isStyleDisabled: isStyleDisabled(audioProfile, voice),
    isBreathingDisabled: isBreathingDisabled(audioProfile),
  };
};

/**
 * React hook to get disabled flags (for convenience in components)
 * Usage: const { isPitchDisabled, isSpeedDisabled } = useVoiceCapabilities(audioProfile, voice);
 */
export const useVoiceCapabilities = (audioProfile?: AudioProfile, voice?: Voice | null) => {
  return getDisabledFlags(audioProfile, voice);
};
